import { Component } from '@angular/core';
import Swal from 'sweetalert2';
import { IGetList } from 'src/app/interfaces/Get/IGetList';
import { Subject, takeUntil } from 'rxjs';
import { CurrentProblemsAddEditComponent } from './current-problems-add-edit/current-problems-add-edit.component';
import { CurrentProblemsExpandComponent } from './current-problems-expand/current-problems-expand.component';
import { IProblemsDetailed } from '../../../../../../interfaces/Problems/IProblemsDetailed';
import { CurrentProblemsService } from '../../../../../../services/currentProblems.service';
import { TranslateService } from '@ngx-translate/core';
import { COLORS } from 'src/styles/colors';
import { SwalToastService } from 'src/app/services/swal.service';
import { PermissionService } from 'src/app/services/permission.service';

@Component({
    selector: 'app-current-problems',
    templateUrl: './current-problems.component.html',
    styleUrls: ['./current-problems.component.css'],
    standalone: false
})
export class CurrentProblemsComponent {
  public addComp = CurrentProblemsAddEditComponent;
  public expandComp = CurrentProblemsExpandComponent;

  private destroy$ = new Subject<void>();

  currentProblems?: IGetList<IProblemsDetailed>;
  isLoading: boolean = false;
  isNoKnownLoading: boolean = false;

  constructor(
    private readonly currentProblemsService: CurrentProblemsService,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService,
    private readonly permissionService: PermissionService
  ) {}

  ngOnInit() {
    this.currentProblemsService.currentProblemLoadingSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: boolean) => {
        this.isLoading = data;
      });

    this.currentProblemsService.currentProblemNoKnownLoadingSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: boolean) => {
        this.isNoKnownLoading = data;
      });

    this.currentProblemsService.currentProblemDataSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: IGetList<IProblemsDetailed>) => {
        this.currentProblems = data;
      });

    this.currentProblemsService.getData();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  hasPermission(permission: string[]): boolean {
    return this.permissionService.can(permission);
  }

  openModalFun(item?: IProblemsDetailed) {
    this.currentProblemsService.currentProblemModalSub.next(true);
    this.currentProblemsService.currentProblemEditSub.next(item);
  }

  deleteFun(problemId: number) {
    Swal.fire({
      text: `${this.translate.instant(
        'deletePlaceholder1'
      )} ${this.translate.instant('Current Problem')}?`,
      showDenyButton: false,
      showCancelButton: true,
      cancelButtonText: `${this.translate.instant('Cancel')}`,
      confirmButtonText: `${this.translate.instant('Ok')}`,
    }).then((result) => {
      if (result.isConfirmed) {
        this.currentProblemsService
          .deleteProblems(problemId.toString())
          .subscribe({
            next: (d) => {
              this.swalToastService.toastSuccess(
                'Problem deleted successfully'
              );
              this.currentProblemsService.getData();
            },
            error: (err) => {
              this.swalToastService.toastError('Unable to delete problem');
            },
          });
      } else if (result.isDenied) {
        Swal.fire(
          `${this.translate.instant('Problem is not deleted')}`,
          '',
          'info'
        );
      }
    });
  }

  stopFun(medicationId: number) {
    Swal.fire({
      text: `${this.translate.instant(
        'stopPlaceholder1'
      )} ${this.translate.instant('Current Problem')}?`,
      showDenyButton: false,
      showCancelButton: true,
      cancelButtonText: `${this.translate.instant('Cancel')}`,
      confirmButtonText: `${this.translate.instant('Ok')}`,
    }).then((result) => {
      if (result.isConfirmed) {
        this.currentProblemsService
          .stopProblem(medicationId.toString())
          .subscribe({
            next: (d) => {
              this.swalToastService.toastSuccess(
                'Current Problem stopped successfully'
              );
              this.currentProblemsService.getData();
              this.currentProblemsService.getData();
            },
            error: (err) => {
              this.swalToastService.toastError(
                'Unable to stop Current Problem'
              );
            },
          });
      } else if (result.isDenied) {
        Swal.fire(
          `${this.translate.instant('Current Problem is not stopped')}`,
          '',
          'info'
        );
      }
    });
  }

  handleToggleFun(preview: boolean, switchValue: boolean, unknownid?: string) {
    if (!preview) {
      if (switchValue) {
        this.currentProblemsService.currentProblemNoKnownLoadingSub.next(true);
        this.currentProblemsService.addNoKnownProblem().subscribe({
          next: () => {
            this.swalToastService.toastSuccess(
              'No known Current Problems set successfully'
            );
            this.currentProblemsService.getData();
          },
          error: (err) => {
            this.swalToastService.toastError('Unable to set No Known Problems');
            console.error('Error setting No Known Problems:', err);
          },
        });
      } else {
        if (unknownid) {
          this.currentProblemsService.currentProblemNoKnownLoadingSub.next(
            true
          );
          this.currentProblemsService
            .deleteNoKnownProblems(unknownid)
            .subscribe({
              next: () => {
                this.swalToastService.toastSuccess(
                  'No known Problems unset successfully'
                );
                this.currentProblemsService.getData();
              },
              error: (err) => {
                this.swalToastService.toastError(
                  'Unable to unset No Known Problems'
                );
                console.error('Error unsetting No Known Problems:', err);
              },
            });
        }
      }
    }
  }

  expandSet = new Set<number>();
  onExpandChange(id: number, checked: boolean): void {
    if (checked) {
      this.expandSet.add(id);
    } else {
      this.expandSet.delete(id);
    }
  }
}
